import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '@shared/service/storage.service';
import { ITokenState } from '../models/ITokenState';
import { STORAGE_KEYS } from '@shared/constants/StorageKeys';

@Injectable({
  providedIn: 'root',
})
export class TokenStore {
  private readonly _storage = inject(StorageService);
  private readonly _router = inject(Router);

  private readonly state = signal<ITokenState>({
    token: null,
    refreshToken: null,
    expired: null,
  });

  /** Refresh-token expiry (UTC) as sent by the API; follows refreshes made in other tabs too. */
  readonly expired = computed(() => this.state().expired);

  constructor() {
    this.loadFromStorage();
    window.addEventListener('storage', event => {
      if (event.key === this._storage.buildKey(STORAGE_KEYS.TOKEN_STORAGE_KEY)) {
        const parsed = event.newValue ? (JSON.parse(event.newValue) as ITokenState) : null;
        if (!parsed?.token) {
          this._router.navigate(['/']);
        } else {
          this.loadFromStorage();
        }
      }
    });
  }

  get token() {
    return this.state().token;
  }

  get refreshToken() {
    return this.state().refreshToken;
  }

  setTokens(token: string, refreshToken: string, expired: string) {
    this.state.set({ token, refreshToken, expired });
    this._storage.set(STORAGE_KEYS.TOKEN_STORAGE_KEY, this.state());
  }

  clear() {
    this.setTokens(null, null, null);
  }

  loadFromStorage() {
    const saved = this._storage.get<ITokenState>(STORAGE_KEYS.TOKEN_STORAGE_KEY);
    if (saved) this.state.set(saved);
  }

  updateTokens(data?) {
    if (data) {
      const { token, refreshToken, expired } = data;
      this.setTokens(token, refreshToken, expired);
    }
  }
}
